/** @typedef {import("./resources").FieldDef} FieldDef */
import React, {useState} from "react";
import {
    Alert,
    Button,
    Form,
    FormFeedback,
    FormGroup,
    Input,
    InputGroup,
    InputGroupText,
    Label,
    Spinner,
} from "reactstrap";
import {parseApiError} from "../../shared/utils/apiErrors";
import InfoTip from "../../shared/components/InfoTip";
import TagsInput from "../../shared/components/TagsInput";
import IconLabel from "../../shared/components/IconLabel";
import {CheckIcon, XIcon} from "../../shared/components/icons";

/**
 * Generic form driven by field definitions (see resources/index.js).
 * `onSubmit` receives the API payload and should throw (axios error) on failure.
 *
 * @param {{
 *   fields: FieldDef[],
 *   initial?: Record<string, any>,
 *   submitLabel: string,
 *   onSubmit: (payload: Record<string, any>) => Promise<void>,
 *   onCancel: () => void,
 * }} props
 */
const ResourceForm = ({fields, initial, submitLabel, onSubmit, onCancel}) => {
    const [values, setValues] = useState(() =>
        Object.fromEntries(
            fields.map(({name, type}) => {
                const value = String(initial?.[name] ?? "");
                // The API sends "09:00:00"; <input type="time"> wants "09:00".
                return [name, type === "time" ? value.slice(0, 5) : value];
            })
        )
    );
    const [errors, setErrors] = useState({general: null, fields: {}});
    const [submitting, setSubmitting] = useState(false);

    const handleChange = (event) => {
        const {name, value} = event.target;
        setValues((v) => ({...v, [name]: value}));
    };

    const missingRequired = fields.some(({name, required}) => required && !values[name].trim());

    const handleSubmit = async (event) => {
        event.preventDefault();
        setErrors({general: null, fields: {}});
        setSubmitting(true);
        try {
            await onSubmit(
                Object.fromEntries(
                    fields.map(({name, nullable}) => {
                        const value = values[name].trim();
                        // Nullable fields: send null rather than "".
                        return [name, nullable && !value ? null : value];
                    })
                )
            );
        } catch (error) {
            setErrors(parseApiError(error));
            setSubmitting(false);
        }
    };

    return (
        <Form onSubmit={handleSubmit} noValidate>
            {errors.general && <Alert color="danger">{errors.general}</Alert>}
            {fields.map(
                ({name, label, type, required, maxLength, step, options, help, placeholder, icon: FieldIcon}) => {
                    const textInput = (
                        <Input
                            id={name}
                            name={name}
                            // select and tags fields are rendered by their own branches.
                            type={/** @type {"text"|"tel"|"email"|"textarea"|"number"|"time"} */ (type)}
                            step={type === "number" ? (step ?? "1") : undefined}
                            min={type === "number" ? "0" : undefined}
                            rows={type === "textarea" ? 4 : undefined}
                            maxLength={maxLength}
                            value={values[name]}
                            onChange={handleChange}
                            invalid={Boolean(errors.fields[name])}
                        />
                    );

                    return (
                        <FormGroup key={name}>
                            {/* The tooltip sits beside the label, not inside it, so the label's
                        text (and the input's accessible name) stays just the label. */}
                            <div className="d-flex align-items-center gap-1 mb-2">
                                <Label for={name} className="mb-0">
                                    {label}
                                    {required && " *"}
                                </Label>
                                {help && <InfoTip>{help}</InfoTip>}
                            </div>
                            {type === "select" ? (
                                <Input
                                    id={name}
                                    name={name}
                                    type="select"
                                    value={values[name]}
                                    onChange={handleChange}
                                    invalid={Boolean(errors.fields[name])}
                                >
                                    {/* Keep a saved value that the option list doesn't know. */}
                                    {values[name] && !options?.some((o) => o.value === values[name]) && (
                                        <option value={values[name]}>{values[name]}</option>
                                    )}
                                    {options?.map((o) => (
                                        <option key={o.value} value={o.value}>
                                            {o.label}
                                        </option>
                                    ))}
                                </Input>
                            ) : type === "tags" ? (
                                <TagsInput
                                    id={name}
                                    value={values[name]}
                                    maxLength={maxLength}
                                    invalid={Boolean(errors.fields[name])}
                                    placeholder={placeholder}
                                    onChange={(value) => setValues((v) => ({...v, [name]: value}))}
                                />
                            ) : FieldIcon ? (
                                // The icon sits before the value, inside the input's box.
                                <InputGroup>
                                    <InputGroupText className="bg-white">
                                        <FieldIcon />
                                    </InputGroupText>
                                    {textInput}
                                </InputGroup>
                            ) : (
                                textInput
                            )}
                            {/* d-block: inside an input group the feedback is not the input's sibling. */}
                            <FormFeedback className={errors.fields[name] ? "d-block" : undefined}>
                                {errors.fields[name]}
                            </FormFeedback>
                        </FormGroup>
                    );
                }
            )}
            {/* Right-aligned, cancel then the main action: same as the modals' footers. */}
            <div className="d-flex justify-content-end gap-2">
                <Button color="secondary" outline type="button" onClick={onCancel} disabled={submitting}>
                    <IconLabel icon={<XIcon />}>Cancelar</IconLabel>
                </Button>
                <Button color="primary" type="submit" disabled={submitting || missingRequired}>
                    {submitting ? <Spinner size="sm" /> : <IconLabel icon={<CheckIcon />}>{submitLabel}</IconLabel>}
                </Button>
            </div>
        </Form>
    );
};

export default ResourceForm;
